import { safeStorage } from '@/lib/safeStorage';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import {
  useItineraries,
  type SavedPlace,
  type GroupedItineraries,
} from '../hooks/useItineraries';
import type { TripGrouping } from '../hooks/useSettings';
import type { RankedPlace } from '../hooks/useLLM';
import type { FindMoreStage } from '../services/placeSearch';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { reportError } from '../lib/reportError';
import { useUiFeedbackContext } from './UiFeedbackContext';

// Re-export for consumers
export type { GroupedItineraries, SavedPlace };

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------
interface TripsContextValue {
  // ── Data ──
  groupedItineraries: GroupedItineraries[];
  itinerariesLoading: boolean;

  // ── Saved-trips-screen UI state ──
  selectNewestTrip: boolean;
  setSelectNewestTrip: (v: boolean) => void;
  savedTripsResetCounter: number;
  setSavedTripsResetCounter: React.Dispatch<React.SetStateAction<number>>;
  loadingMoreForTrip: boolean;
  setLoadingMoreForTrip: (v: boolean) => void;

  // ── My-Trips tab highlight ──
  tripSavedHighlight: boolean;
  tripSavedToast: boolean;
  setTripSavedToast: (v: boolean) => void;
  neverTappedMyTrips: boolean;
  highlightMyTrips: boolean;

  // ── Current-results trip tracking (instant save/remove) ──
  currentResultsTripId: string | null;
  setCurrentResultsTripId: (id: string | null) => void;
  instantSavedPlaceIds: Set<string>;
  setInstantSavedPlaceIds: (ids: Set<string>) => void;
  instantSavedPlacesRef: React.MutableRefObject<RankedPlace[]>;

  // ── Find-more state ──
  findMoreStage: FindMoreStage;
  setFindMoreStage: (stage: FindMoreStage) => void;
  searchLimitReached: boolean;
  setSearchLimitReached: (v: boolean) => void;

  // ── Core CRUD (used by SavedTripsScreen) ──
  loadSavedItineraries: (grouping?: TripGrouping, force?: boolean) => Promise<void>;
  handleDeleteItinerary: (id: string) => Promise<void>;
  handleDeleteGroup: (itineraryIds: string[]) => Promise<void>;
  handleDeletePlace: (placeId: string, itineraryId: string) => Promise<void>;
  handleRenameTrip: (id: string, newTitle: string) => Promise<boolean>;
  handleMergeTrips: (
    targetId: string,
    sourceIds: string[],
    mergedMetadata: {
      location: string;
      time_available: string;
      transport: string;
      vibes: string[];
      ai_mode: string;
      sourceLocations?: string[];
      renamedTitles?: { title: string; placeCount: number }[];
      pinnedTitle?: string | null;
      pinned?: boolean;
    },
    dedupedPlaces: SavedPlace[],
  ) => Promise<void>;
  handleAddPlacesToTrip: (itineraryId: string, places: RankedPlace[]) => Promise<void>;
  handleShareTrip: (itineraryId: string) => Promise<string | null>;
  handleUnshareTrip: (itineraryId: string) => Promise<boolean>;
  /** H-213 v2: pin / unpin a trip. Up to 2 pins per user (FIFO replacement
   *  on slot 2). handleSetPinned returns true on success, false on no-op. */
  handleSetPinned: (itineraryId: string) => Promise<boolean>;
  handleUnpin: (itineraryId: string) => Promise<boolean>;

  // ── Highlight helpers ──
  startTripSavedHighlight: () => void;
  clearHighlight: () => void;
  resetTripsState: () => void;

  // ── Pass-through from useItineraries (used by save flows in App.tsx) ──
  saveItinerary: ReturnType<typeof useItineraries>['saveItinerary'];
  addPlacesToItinerary: ReturnType<typeof useItineraries>['addPlacesToItinerary'];
  deleteSavedPlaceByPlaceId: ReturnType<typeof useItineraries>['deleteSavedPlaceByPlaceId'];
  regenerateTripTitle: ReturnType<typeof useItineraries>['regenerateTripTitle'];
}

const TripsContext = createContext<TripsContextValue | null>(null);

export function useTripsContext() {
  const ctx = useContext(TripsContext);
  if (!ctx) throw new Error('useTripsContext must be used within <TripsProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
interface TripsProviderProps {
  children: ReactNode;
  tripGrouping: TripGrouping;
  referralCode: string | null;
}

export function TripsProvider({
  children,
  tripGrouping,
  referralCode,
}: TripsProviderProps) {
  const feedback = useUiFeedbackContext();
  // Shared context-based hooks — safe to call in multiple components
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const userId = user?.id;

  const {
    saveItinerary,
    fetchItineraries,
    deleteItinerary,
    deleteGroup,
    addPlacesToItinerary,
    deleteSavedPlace,
    deleteSavedPlaceByPlaceId,
    updateItineraryTitle,
    regenerateTripTitle,
    mergeItineraries,
    shareTrip,
    unshareTrip,
    setPinnedItinerary,
    unpinItinerary,
    loading: itinerariesLoading,
  } = useItineraries();

  // ── State ──
  const [groupedItineraries, setGroupedItineraries] = useState<GroupedItineraries[]>([]);
  const [selectNewestTrip, setSelectNewestTrip] = useState(false);
  const [savedTripsResetCounter, setSavedTripsResetCounter] = useState(0);
  const [loadingMoreForTrip, setLoadingMoreForTrip] = useState(false);
  const [currentResultsTripId, setCurrentResultsTripId] = useState<string | null>(null);
  const [instantSavedPlaceIds, setInstantSavedPlaceIds] = useState<Set<string>>(new Set());
  const instantSavedPlacesRef = useRef<RankedPlace[]>([]);
  const [findMoreStage, setFindMoreStage] = useState<FindMoreStage>(0);
  const [searchLimitReached, setSearchLimitReached] = useState(false);

  // ── Highlight state ──
  const [tripSavedHighlight, setTripSavedHighlight] = useState(false);
  const [tripSavedToast, setTripSavedToast] = useState(false);
  const tripSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [neverTappedMyTrips, setNeverTappedMyTrips] = useState(() =>
    safeStorage.getItem('myTrips_tapped') !== '1',
  );

  const highlightMyTrips = neverTappedMyTrips || tripSavedHighlight;

  useEffect(() => {
    return () => {
      if (tripSavedTimerRef.current) clearTimeout(tripSavedTimerRef.current);
    };
  }, []);

  // ── Load itineraries with dedup guard ──
  const fetchInProgressRef = useRef(false);
  const pendingTripGroupingRef = useRef<TripGrouping | null>(null);
  const pendingForceReloadRef = useRef(false);

  const loadSavedItineraries = useCallback(
    async (grouping?: TripGrouping, force?: boolean) => {
      const groupingToUse = grouping ?? tripGrouping;
      if (!userId) return;

      if (fetchInProgressRef.current) {
        pendingTripGroupingRef.current = groupingToUse;
        if (force) pendingForceReloadRef.current = true;
        return;
      }

      fetchInProgressRef.current = true;

      try {
        let itineraries: GroupedItineraries[];
        try {
          itineraries = await fetchItineraries(userId, groupingToUse);
        } catch (err) {
          // Keep whatever is on screen; "no saved trips" must never mean "the load failed".
          reportError('trips.load', err, { userId });
          feedback.showErrorToast(t.toasts.loadFailed);
          return;
        }
        setGroupedItineraries(itineraries);

        const needsReload =
          pendingForceReloadRef.current ||
          (pendingTripGroupingRef.current &&
            pendingTripGroupingRef.current !== groupingToUse);

        if (needsReload) {
          const nextGrouping = pendingTripGroupingRef.current ?? groupingToUse;
          pendingTripGroupingRef.current = null;
          pendingForceReloadRef.current = false;
          fetchInProgressRef.current = false;
          await loadSavedItineraries(nextGrouping);
          return;
        }
      } finally {
        fetchInProgressRef.current = false;
        pendingTripGroupingRef.current = null;
        pendingForceReloadRef.current = false;
      }
    },
    [userId, fetchItineraries, tripGrouping, feedback, t],
  );

  // ── Highlight helpers ──
  const startTripSavedHighlight = useCallback(() => {
    if (tripSavedTimerRef.current) {
      clearTimeout(tripSavedTimerRef.current);
    }
    setTripSavedHighlight(true);
    setTripSavedToast(true);
    setTimeout(() => setTripSavedToast(false), 4000);
    tripSavedTimerRef.current = setTimeout(() => {
      setTripSavedHighlight(false);
      tripSavedTimerRef.current = null;
    }, 60_000);
  }, []);

  const clearHighlight = useCallback(() => {
    if (neverTappedMyTrips) {
      safeStorage.setItem('myTrips_tapped', '1');
      setNeverTappedMyTrips(false);
    }
    setTripSavedHighlight(false);
    if (tripSavedTimerRef.current) {
      clearTimeout(tripSavedTimerRef.current);
      tripSavedTimerRef.current = null;
    }
  }, [neverTappedMyTrips]);

  // ── CRUD handlers ──
  const handleDeleteItinerary = useCallback(
    async (id: string) => {
      const success = await deleteItinerary(id);
      if (success) await loadSavedItineraries();
      else feedback.showErrorToast(t.toasts.deleteFailed);
    },
    [deleteItinerary, loadSavedItineraries, feedback, t],
  );

  const handleRenameTrip = useCallback(
    async (id: string, newTitle: string): Promise<boolean> => {
      const success = await updateItineraryTitle(id, newTitle);
      if (success) { await loadSavedItineraries(); feedback.showCreditToast(t.toasts.renamed, 2500); }
      else feedback.showErrorToast(t.toasts.renameFailed);
      return success;
    },
    [updateItineraryTitle, loadSavedItineraries, feedback, t],
  );

  const handleDeleteGroup = useCallback(
    async (itineraryIds: string[]) => {
      const success = await deleteGroup(itineraryIds);
      if (success) await loadSavedItineraries();
      else feedback.showErrorToast(t.toasts.deleteFailed);
    },
    [deleteGroup, loadSavedItineraries, feedback, t],
  );

  const handleMergeTrips = useCallback(
    async (
      targetId: string,
      sourceIds: string[],
      mergedMetadata: {
        location: string;
        time_available: string;
        transport: string;
        vibes: string[];
        ai_mode: string;
        sourceLocations?: string[];
        renamedTitles?: { title: string; placeCount: number }[];
      },
      dedupedPlaces: SavedPlace[],
    ) => {
      await mergeItineraries(targetId, sourceIds, mergedMetadata, dedupedPlaces);
      await loadSavedItineraries(undefined, true);
    },
    [mergeItineraries, loadSavedItineraries],
  );

  const handleDeletePlace = useCallback(
    async (placeId: string, _itineraryId: string) => {
      const success = await deleteSavedPlace(placeId);
      if (success) await loadSavedItineraries();
      else feedback.showErrorToast(t.toasts.deleteFailed);
    },
    [deleteSavedPlace, loadSavedItineraries, feedback, t],
  );

  const handleAddPlacesToTrip = useCallback(
    async (itineraryId: string, places: RankedPlace[]) => {
      if (!userId) return;
      const success = await addPlacesToItinerary(itineraryId, places);
      if (success) await loadSavedItineraries();
      else feedback.showErrorToast(t.toasts.addPlacesFailed);
    },
    [userId, addPlacesToItinerary, loadSavedItineraries, feedback, t],
  );

  const handleShareTrip = useCallback(
    async (itineraryId: string): Promise<string | null> => {
      let url = await shareTrip(itineraryId, language);
      // Referral codes exist for every account (DB trigger); never fall back to the account id in a public link.
      if (url && referralCode) {
        url += `?ref=${referralCode}`;
      }
      if (url && !safeStorage.getItem('tb_shared')) {
        safeStorage.setItem('tb_shared', '1');
        feedback.showToast('firstShare');
      }
      if (url) await loadSavedItineraries();
      return url;
    },
    [shareTrip, language, referralCode, loadSavedItineraries, feedback],
  );

  const handleUnshareTrip = useCallback(
    async (itineraryId: string): Promise<boolean> => {
      const success = await unshareTrip(itineraryId);
      if (success) await loadSavedItineraries();
      return success;
    },
    [unshareTrip, loadSavedItineraries],
  );

  const handleSetPinned = useCallback(
    async (itineraryId: string): Promise<boolean> => {
      const ok = await setPinnedItinerary(itineraryId);
      if (ok) await loadSavedItineraries(undefined, true);
      return ok;
    },
    [setPinnedItinerary, loadSavedItineraries],
  );

  const handleUnpin = useCallback(
    async (itineraryId: string): Promise<boolean> => {
      const success = await unpinItinerary(itineraryId);
      if (success) await loadSavedItineraries(undefined, true);
      return success;
    },
    [unpinItinerary, loadSavedItineraries],
  );

  // ── Reset on sign-out ──
  const resetTripsState = useCallback(() => {
    setGroupedItineraries([]);
    setSelectNewestTrip(false);
    setCurrentResultsTripId(null);
    setInstantSavedPlaceIds(new Set());
    instantSavedPlacesRef.current = [];
    setFindMoreStage(0);
    setSearchLimitReached(false);
  }, []);

  // ── Context value ──
  const value: TripsContextValue = {
    groupedItineraries,
    itinerariesLoading,
    selectNewestTrip,
    setSelectNewestTrip,
    savedTripsResetCounter,
    setSavedTripsResetCounter,
    loadingMoreForTrip,
    setLoadingMoreForTrip,
    tripSavedHighlight,
    tripSavedToast,
    setTripSavedToast,
    neverTappedMyTrips,
    highlightMyTrips,
    currentResultsTripId,
    setCurrentResultsTripId,
    instantSavedPlaceIds,
    setInstantSavedPlaceIds,
    instantSavedPlacesRef,
    findMoreStage,
    setFindMoreStage,
    searchLimitReached,
    setSearchLimitReached,
    loadSavedItineraries,
    handleDeleteItinerary,
    handleDeleteGroup,
    handleDeletePlace,
    handleRenameTrip,
    handleMergeTrips,
    handleAddPlacesToTrip,
    handleShareTrip,
    handleUnshareTrip,
    handleSetPinned,
    handleUnpin,
    startTripSavedHighlight,
    clearHighlight,
    resetTripsState,
    saveItinerary,
    addPlacesToItinerary,
    deleteSavedPlaceByPlaceId,
    regenerateTripTitle,
  };

  return <TripsContext.Provider value={value}>{children}</TripsContext.Provider>;
}
